import React from "react";
import { connect } from "react-redux";
import { withTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import Footer from "../Footer/FooterBottom";
import { getToken } from "../../context/auth";
import AiStatsModal from "../Common/AiStatsModal";
import {
    initChatbot,
    deleteChatHistory,
    deleteChatSession,
    clearActiveChat,
    startNewChat,
    loadChatSession,
    stopChatStream,
    sendChatMessage
} from "./actions/chatbotActions";

// Dedicated PureComponent for the input section to isolate re-render on typing
class ChatInput extends React.PureComponent {
    state = {
        inputText: "",
        passCash: false,
        model: "deepseek"
    };

    hasNonArabicLetters = (text) => {
        const letters = text.match(/\p{L}/gu);
        if (!letters) return false;
        return letters.some(char => {
            const code = char.charCodeAt(0);
            const isArabicRange = (code >= 0x0600 && code <= 0x06FF) ||
                (code >= 0x0750 && code <= 0x077F) ||
                (code >= 0x08A0 && code <= 0x08FF) ||
                (code >= 0xFB50 && code <= 0xFDFF) ||
                (code >= 0xFE70 && code <= 0xFEFF);
            return !isArabicRange;
        });
    };

    handleSend = (e) => {
        if (e) e.preventDefault();
        const { inputText, passCash, model } = this.state;
        const { onSend, loading } = this.props;

        if (inputText.trim().length >= 3 && !this.hasNonArabicLetters(inputText) && !loading) {
            onSend(inputText.trim(), passCash, model);
            this.setState({ inputText: "" });
        }
    };

    handleKeyPress = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            this.handleSend();
        }
    };

    render() {
        const { inputText, passCash, model } = this.state;
        const { loading, t } = this.props;
        const isInputShort = inputText.length > 0 && inputText.length < 3;
        const isNonArabic = this.hasNonArabicLetters(inputText);

        return (
            <form onSubmit={this.handleSend}>
                <div className="input-container">
                    <textarea
                        value={inputText}
                        onChange={(e) => this.setState({ inputText: e.target.value })}
                        onKeyDown={this.handleKeyPress}
                        placeholder={t("Pages.Chatbot.Placeholder")}
                        className="input-textarea"
                        disabled={loading}
                    />
                    {loading ? (
                        <button
                            type="button"
                            className="stop-button animate__animated animate__pulse animate__infinite"
                            onClick={this.props.onStop}
                            title={t("Pages.Chatbot.Stop")}
                        >
                            <i className="fal fa-stop-circle"></i>
                        </button>
                    ) : (
                        <button
                            type="submit"
                            className="send-button"
                            disabled={inputText.trim().length < 3 || isNonArabic}
                            title={t("Pages.Chatbot.Send")}
                        >
                            <i className="fal fa-paper-plane"></i>
                        </button>
                    )}
                </div>
                <div className="input-options-row">
                    <label className="passcash-label">
                        <input
                            type="checkbox"
                            checked={passCash}
                            onChange={(e) => this.setState({ passCash: e.target.checked })}
                            disabled={loading}
                            className="passcash-checkbox"
                        />
                        <span className="passcash-text">{t("Pages.Chatbot.PassCash")}</span>
                    </label>
                    <div className="model-selector-container">
                        <select
                            value={model}
                            onChange={(e) => this.setState({ model: e.target.value })}
                            disabled={loading}
                            className="model-select"
                        >
                            <option value="gemini">Gemini Flash 3.1 LITE</option>
                            <option value="deepseek">DeepSeek Flash</option>
                        </select>
                    </div>
                </div>
                {isNonArabic ? (
                    <div className="warning-message mt-2">
                        <i className="fal fa-exclamation-triangle"></i>
                        <span>{t("Pages.Chatbot.ArabicOnlyError")}</span>
                    </div>
                ) : isInputShort ? (
                    <div className="warning-message mt-2">
                        <i className="fal fa-exclamation-triangle"></i>
                        <span>{t("Pages.Chatbot.MinLettersError")}</span>
                    </div>
                ) : null}
            </form>
        );
    }
}

class Chatbot extends React.PureComponent {
    state = {
        searchTerm: "",
        showSidebar: window.innerWidth >= 768,
        confirmDelete: false,
        confirmClear: false,
        fullscreenMessageHtml: null,
        fullscreenMessageQuestion: null,
        expandedStatsIndex: -1,
        requestTimerSeconds: 0
    };

    messagesEndRef = React.createRef();
    requestTimerInterval = null;

    toggleStats = (index) => {
        this.setState(prevState => ({
            expandedStatsIndex: prevState.expandedStatsIndex === index ? -1 : index
        }));
    };

    startRequestTimer = () => {
        this.stopRequestTimer();
        this.setState({ requestTimerSeconds: 0 });
        this.requestTimerInterval = setInterval(() => {
            this.setState(prevState => ({
                requestTimerSeconds: prevState.requestTimerSeconds + 1
            }));
        }, 1000);
    };

    stopRequestTimer = () => {
        if (this.requestTimerInterval) {
            clearInterval(this.requestTimerInterval);
            this.requestTimerInterval = null;
        }
    };

    handleDownloadResult = (htmlContent, question = "") => {
        let safeQuestion = question
            ? question.replace(/[\\/:*?"<>|]/g, "").trim().substring(0, 50)
            : "اجابة_المساعد";
        if (!safeQuestion) safeQuestion = "اجابة_المساعد";

        const now = new Date();
        const dateStr = now.getFullYear() + "-" +
            String(now.getMonth() + 1).padStart(2, '0') + "-" +
            String(now.getDate()).padStart(2, '0') + "_" +
            String(now.getHours()).padStart(2, '0') + "-" +
            String(now.getMinutes()).padStart(2, '0') + "-" +
            String(now.getSeconds()).padStart(2, '0');

        const filename = `${safeQuestion}_${dateStr}.html`;

        const element = document.createElement("a");
        const file = new Blob([`
            <!DOCTYPE html>
            <html lang="ar" dir="rtl">
            <head>
                <meta charset="utf-8">
                <title>إجابة مساعد الذكاء الاصطناعي</title>
                <link rel="preconnect" href="https://fonts.googleapis.com">
                <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
                <link href="https://fonts.googleapis.com/css2?family=Cairo:wght@300;400;600;700&family=Droid+Arabic+Naskh:wght@400;700&display=swap" rel="stylesheet">
                <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css">
                <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.15.4/css/all.min.css">
                <style>
                    body {
                        font-family: 'Cairo', 'Droid Arabic Naskh', Arial, sans-serif;
                        padding: 30px;
                        background-color: #fdfaf5;
                        color: #0e2c53;
                        line-height: 1.8;
                    }
                    table {
                        width: 100%;
                        border-collapse: collapse;
                        margin: 20px 0;
                    }
                    th, td {
                        padding: 12px 15px;
                        text-align: right;
                        border-bottom: 1px solid #ebd9c5;
                    }
                    th {
                        background-color: #0e2c53;
                        color: white;
                    }
                    tr:nth-child(even) {
                        background-color: #fbf6ee;
                    }
                    a {
                        display: inline-block;
                        background-color: #3b82f6;
                        color: white;
                        padding: 10px 20px;
                        margin: 5px;
                        border-radius: 8px;
                        text-decoration: none;
                        font-weight: bold;
                    }
                </style>
            </head>
            <body>
                ${htmlContent}
            </body>
            </html>
        `], { type: 'text/html' });
        element.href = URL.createObjectURL(file);
        element.download = filename;
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);
    };

    handleKeyDown = (e) => {
        if (e.key === "Escape") {
            if (this.state.fullscreenMessageHtml) {
                this.setState({ fullscreenMessageHtml: null, fullscreenMessageQuestion: null });
            } else if (this.state.expandedStatsIndex !== -1) {
                this.setState({ expandedStatsIndex: -1 });
            }
        }
    };

    componentDidMount() {
        // Initialize chatbot history and restore last active session
        this.props.initChatbot();

        setTimeout(() => this.scrollToBottom(), 300);
        window.addEventListener("keydown", this.handleKeyDown);
    }

    componentWillUnmount() {
        window.removeEventListener("keydown", this.handleKeyDown);
        this.stopRequestTimer();
    }

    componentDidUpdate(prevProps) {
        // Scroll to bottom when new messages arrive or current message content changes (streaming)
        const messagesChanged = prevProps.messages.length !== this.props.messages.length;
        const currentMsgStreaming =
            this.props.messages.length > 0 &&
            prevProps.messages.length > 0 &&
            prevProps.messages[prevProps.messages.length - 1].text !== this.props.messages[this.props.messages.length - 1].text;

        if (messagesChanged || currentMsgStreaming) {
            this.scrollToBottom();
        }

        // Handle request timer
        if (!prevProps.loading && this.props.loading) {
            this.startRequestTimer();
        } else if (prevProps.loading && !this.props.loading) {
            this.stopRequestTimer();
        }
    }

    scrollToBottom = () => {
        if (this.messagesEndRef.current) {
            this.messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
    };

    handleResend = () => {
        const { messages } = this.props;
        const lastUserMsg = messages.slice().reverse().find(m => m.sender === "user");
        if (lastUserMsg) {
            this.props.sendChatMessage(lastUserMsg.text);
        }
    };

    toggleSidebar = () => {
        this.setState(prevState => ({ showSidebar: !prevState.showSidebar }));
    };

    handleSessionClick = (session) => {
        this.props.loadChatSession(session);
        // On mobile, automatically close sidebar after choosing a session
        if (window.innerWidth < 768) {
            this.setState({ showSidebar: false });
        }
    };

    handleNewChat = () => {
        this.props.startNewChat();
    };

    handleDeleteHistory = () => {
        if (window.confirm(this.props.t("Pages.Chatbot.ConfirmDeleteHistory"))) {
            this.props.deleteChatHistory();
        }
    };

    handleClearChat = () => {
        if (window.confirm(this.props.t("Pages.Chatbot.ConfirmClearChat"))) {
            this.props.clearActiveChat();
        }
    };

    renderLoginRequired = () => {
        const { t } = this.props;
        return (
            <>
                <Helmet>
                    <title>{t("Pages.Chatbot.title")} | {t("alarabeyya")}</title>
                </Helmet>

                <style dangerouslySetInnerHTML={{
                    __html: `
                    .chatbot-page-section {
                        position: relative;
                        overflow: hidden;
                        background: linear-gradient(135deg, rgba(255, 248, 235, 0.9) 0%, rgba(253, 242, 226, 0.9) 100%),
                                    url('/assets/images/about/bg.png') center center / cover no-repeat;
                        padding: 80px 15px;
                        min-height: calc(100vh - 120px);
                        display: flex;
                        align-items: center;
                        justify-content: center;
                    }
                    .chatbot-blob {
                        position: absolute;
                        border-radius: 50%;
                        filter: blur(80px);
                        z-index: 1;
                        opacity: 0.6;
                        animation: float 20s infinite alternate ease-in-out;
                    }
                    .blob-1 {
                        width: 400px;
                        height: 400px;
                        background: radial-gradient(circle, rgba(146, 39, 143, 0.25) 0%, rgba(255,255,255,0) 70%);
                        top: -100px;
                        right: -100px;
                    }
                    .blob-2 {
                        width: 500px;
                        height: 500px;
                        background: radial-gradient(circle, rgba(255, 82, 0, 0.2) 0%, rgba(255,255,255,0) 70%);
                        bottom: -150px;
                        left: -150px;
                        animation-delay: -5s;
                    }
                    @keyframes float {
                        0% { transform: translate(0, 0) scale(1); }
                        50% { transform: translate(40px, -60px) scale(1.1); }
                        100% { transform: translate(-20px, 20px) scale(0.95); }
                    }
                    .lock-card {
                        background: rgba(255, 255, 255, 0.85);
                        backdrop-filter: blur(12px);
                        border: 1px solid rgba(235, 217, 197, 0.5);
                        border-radius: 24px;
                        padding: 50px 30px;
                        max-width: 500px;
                        width: 100%;
                        text-align: center;
                        box-shadow: 0 20px 50px rgba(14, 44, 83, 0.12);
                        z-index: 2;
                        animation: slideUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
                    }
                    .lock-icon-container {
                        width: 90px;
                        height: 90px;
                        border-radius: 50%;
                        background: rgba(146, 39, 143, 0.1);
                        color: #92278f;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        margin: 0 auto 24px;
                        font-size: 36px;
                        animation: pulseLock 2s infinite ease-in-out;
                    }
                    @keyframes pulseLock {
                        0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(146, 39, 143, 0.2); }
                        50% { transform: scale(1.05); box-shadow: 0 0 0 15px rgba(146, 39, 143, 0); }
                    }
                    .lock-title {
                        font-size: 22px;
                        font-weight: 700;
                        color: #0e2c53;
                        margin-bottom: 12px;
                        font-family: "Droid Arabic Naskh", sans-serif;
                    }
                    .lock-text {
                        font-size: 15px;
                        color: #8c857b;
                        line-height: 1.6;
                        margin-bottom: 28px;
                        font-family: "Droid Arabic Naskh", sans-serif;
                    }
                    .login-cta-btn {
                        display: inline-flex;
                        align-items: center;
                        gap: 8px;
                        background: linear-gradient(135deg, #92278f 0%, #b23cae 100%);
                        color: #ffffff !important;
                        font-weight: 700;
                        padding: 12px 30px;
                        border-radius: 14px;
                        box-shadow: 0 8px 20px rgba(146, 39, 143, 0.3);
                        transition: all 0.3s ease;
                        text-decoration: none;
                        font-family: "Droid Arabic Naskh", sans-serif;
                    }
                    .login-cta-btn:hover {
                        transform: translateY(-2px);
                        box-shadow: 0 12px 24px rgba(146, 39, 143, 0.4);
                        text-decoration: none;
                    }
                    .login-cta-btn i {
                        margin-left: 6px;
                        margin-right: 0;
                    }
                `}} />

                <div className="chatbot-page-section">
                    <div className="lock-card">
                        <div className="lock-icon-container">
                            <i className="fal fa-user-lock"></i>
                        </div>
                        <h3 className="lock-title">{t("Pages.Chatbot.LoginRequired") || "تسجيل الدخول مطلوب"}</h3>
                        <p className="lock-text">
                            {t("Pages.Chatbot.LoginRequiredDesc") || "يرجى تسجيل الدخول للوصول إلى مساعد الذكاء الاصطناعي والبدء في طرح الأسئلة."}
                        </p>
                        <a href="/Login" className="login-cta-btn">
                            <i className="fal fa-sign-in-alt"></i>
                            <span>{t("Login") || "تسجيل الدخول"}</span>
                        </a>
                    </div>
                </div>
                <Footer />
            </>
        );
    };

    render() {
        if (!getToken()) {
            return this.renderLoginRequired();
        }

        const { t, messages, history, loading, databaseTimeMs, apiTimeMs, error, errorStatus, activeSessionId } = this.props;
        const { searchTerm, showSidebar, fullscreenMessageHtml, expandedStatsIndex } = this.state;

        // Filter the chat sessions list based on search term, sorting by date descending
        const sortedHistory = history.slice().sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
        const filteredHistory = sortedHistory.filter(session =>
            session.title.toLowerCase().includes(searchTerm.toLowerCase())
        );

        return (
            <>
                <Helmet>
                    <title>{t("Pages.Chatbot.title")} | {t("alarabeyya")}</title>
                </Helmet>

                <style dangerouslySetInnerHTML={{
                    __html: `
                    .chatbot-page-section {
                        position: relative;
                        overflow: hidden;
                        background: linear-gradient(135deg, rgba(255, 248, 235, 0.9) 0%, rgba(253, 242, 226, 0.9) 100%),
                                    url('/assets/images/about/bg.png') center center / cover no-repeat;
                        padding: 50px 15px;
                        min-height: calc(100vh - 120px);
                        display: flex;
                        align-items: center;
                        justify-content: center;
                    }
                    .chatbot-wrapper {
                        display: flex;
                        max-width: 1200px;
                        width: 100%;
                        background: rgba(255, 255, 255, 0.85);
                        backdrop-filter: blur(12px);
                        -webkit-backdrop-filter: blur(12px);
                        border: 1px solid rgba(235, 217, 197, 0.5);
                        border-radius: 24px;
                        height: 700px;
                        overflow: hidden;
                        box-shadow: 0 20px 50px rgba(14, 44, 83, 0.12);
                        position: relative;
                        z-index: 2;
                        animation: slideUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
                    }
                    @keyframes slideUp {
                        0% { opacity: 0; transform: translateY(40px); }
                        100% { opacity: 1; transform: translateY(0); }
                    }
                    .chatbot-sidebar {
                        width: 310px;
                        background-color: rgba(253, 250, 245, 0.7);
                        border-left: 1px solid rgba(235, 217, 197, 0.5);
                        display: flex;
                        flex-direction: column;
                        height: 100%;
                        transition: all 0.3s ease;
                        z-index: 10;
                    }
                    .sidebar-header {
                        padding: 25px 20px;
                        border-bottom: 1px solid rgba(235, 217, 197, 0.5);
                        display: flex;
                        flex-direction: column;
                        gap: 12px;
                    }
                    .sidebar-title-row {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                    }
                    .sidebar-title {
                        font-size: 18px;
                        font-weight: 700;
                        color: #0e2c53;
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        margin: 0;
                        font-family: "Droid Arabic Naskh", sans-serif;
                    }
                    .sidebar-title i {
                        color: #92278f;
                    }
                    .sidebar-search-group {
                        display: flex;
                        border-radius: 12px;
                        overflow: hidden;
                        border: 1px solid rgba(235, 217, 197, 0.6);
                        background: #ffffff;
                        width: 100%;
                        align-items: stretch;
                        transition: all 0.2s ease;
                    }
                    .sidebar-search-group:focus-within {
                        border-color: #92278f;
                        box-shadow: 0 0 0 3px rgba(146, 39, 143, 0.15);
                    }
                    .sidebar-search-group .sidebar-search-container {
                        flex: 1;
                        display: flex;
                        align-items: center;
                        position: relative;
                    }
                    .sidebar-search-group .sidebar-search-input {
                        width: 100%;
                        height: 40px;
                        border: none;
                        border-radius: 0;
                        padding: 10px 36px 10px 14px;
                        font-size: 13.5px;
                        outline: none;
                        background-color: transparent;
                        color: #0e2c53;
                        text-align: right;
                        font-family: inherit;
                    }
                    .sidebar-search-group .sidebar-search-input:focus {
                        box-shadow: none;
                    }
                    .sidebar-search-group .sidebar-search-icon {
                        position: absolute;
                        right: 12px;
                        color: #8c857b;
                        font-size: 14px;
                        pointer-events: none;
                        z-index: 2;
                    }
                    .sidebar-new-chat-btn {
                        border: none;
                        border-right: 1px solid rgba(235, 217, 197, 0.6);
                        background: linear-gradient(135deg, #92278f 0%, #b23cae 100%);
                        color: #ffffff;
                        padding: 0 16px;
                        cursor: pointer;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        gap: 6px;
                        transition: all 0.2s ease;
                        font-size: 13px;
                        font-weight: bold;
                        font-family: "Droid Arabic Naskh", sans-serif;
                        width: auto;
                    }
                    .sidebar-new-chat-btn:hover {
                        background: linear-gradient(135deg, #791f76 0%, #9c3398 100%);
                    }
                    .sidebar-title-actions {
                        display: flex;
                        align-items: center;
                        gap: 8px;
                    }
                    .sidebar-title-delete-btn {
                        background: transparent;
                        border: none;
                        color: #ff5200;
                        cursor: pointer;
                        font-size: 15px;
                        width: 32px;
                        height: 32px;
                        border-radius: 50%;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        transition: all 0.25s ease;
                        outline: none;
                    }
                    .sidebar-title-delete-btn:hover {
                        background-color: rgba(255, 82, 0, 0.1);
                        transform: scale(1.05);
                    }
                    .sidebar-history-list {
                        flex: 1;
                        overflow-y: auto;
                        padding: 15px;
                    }
                    .history-item {
                        padding: 8px 12px;
                        border-radius: 12px;
                        cursor: pointer;
                        margin-bottom: 8px;
                        background-color: transparent;
                        border: 1px solid transparent;
                        transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        gap: 10px;
                        text-align: right;
                        position: relative;
                        overflow: hidden;
                    }
                    .history-item:hover {
                        background-color: rgba(255, 242, 223, 0.8);
                        border-color: rgba(235, 217, 197, 0.8);
                        transform: translateY(-1px);
                    }
                    .history-item.active {
                        background: linear-gradient(135deg, rgba(146, 39, 143, 0.08) 0%, rgba(255, 82, 0, 0.04) 100%);
                        border-color: rgba(146, 39, 143, 0.3);
                        box-shadow: 0 4px 15px rgba(146, 39, 143, 0.05);
                    }
                    .history-item.active .history-item-title {
                        color: #92278f;
                    }
                    .history-item-content {
                        flex: 1;
                        min-width: 0;
                        display: flex;
                        flex-direction: column;
                        gap: 2px;
                    }
                    .history-item-title {
                        font-size: 13.5px;
                        color: #0e2c53;
                        font-weight: bold;
                        white-space: nowrap;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        font-family: "Droid Arabic Naskh", sans-serif;
                    }
                    .history-item-date {
                        font-size: 10.5px;
                        color: #8c857b;
                        display: flex;
                        align-items: center;
                        gap: 4px;
                    }
                    .history-item-delete-btn {
                        background: transparent;
                        border: none;
                        color: #8c857b;
                        opacity: 0;
                        width: 28px;
                        height: 28px;
                        border-radius: 8px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        cursor: pointer;
                        transition: all 0.2s ease;
                        font-size: 12px;
                        outline: none;
                    }
                    .history-item:hover .history-item-delete-btn, .history-item.active .history-item-delete-btn {
                        opacity: 0.6;
                    }
                    .history-item-delete-btn:hover {
                        opacity: 1 !important;
                        background-color: rgba(255, 82, 0, 0.1);
                        color: #ff5200;
                    }
                    .no-history-text {
                        color: #8c857b;
                        font-size: 14px;
                        text-align: center;
                        margin-top: 30px;
                        font-family: "Droid Arabic Naskh", sans-serif;
                    }
                    .chatbot-main {
                        flex: 1;
                        display: flex;
                        flex-direction: column;
                        height: 100%;
                        background-color: rgba(255, 255, 255, 0.9);
                    }
                    .chat-header {
                        padding: 5px 10px;
                        border-bottom: 1px solid rgba(235, 217, 197, 0.5);
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        background: rgba(253, 250, 245, 0.5);
                    }
                    .chat-header-info {
                        display: flex;
                        align-items: center;
                        gap: 12px;
                    }
                    .chat-header-title {
                        display: flex;
                        align-items: center;
                        gap: 2px;
                        font-size: 19px;
                        font-weight: 700;
                        color: #0e2c53;
                        margin: 0;
                        line-height: 70px;
                        font-family: "Droid Arabic Naskh", sans-serif;
                    }
                    .chat-header-subtitle {
                        font-size: 12px;
                        color: #8c857b;
                        margin: 0;
                        font-family: "Droid Arabic Naskh", sans-serif;
                    }
                    .chat-header-actions {
                        display: flex;
                        gap: 10px;
                        align-items: center;
                    }
                    .chat-messages-area {
                        flex: 1;
                        overflow-y: auto;
                        padding: 30px;
                        background-color: rgba(255, 252, 248, 0.4);
                        display: flex;
                        flex-direction: column;
                        gap: 24px;
                    }
                    .message-row {
                        display: flex;
                        width: 100%;
                    }
                    .message-row.user {
                        justify-content: flex-start;
                    }
                    .message-row.bot {
                        justify-content: flex-end;
                    }
                    .message-bubble-wrapper {
                        max-width: 80%;
                        display: flex;
                        flex-direction: column;
                    }
                    .message-row.user .message-bubble-wrapper {
                        align-items: flex-start;
                    }
                    .message-row.bot .message-bubble-wrapper {
                        max-width: 100%;
                        width: 100%;
                        align-items: flex-start;
                    }
                    .message-row.bot .message-bubble {
                        width: 100%;
                    }
                    .message-bubble {
                        padding: 16px 22px;
                        line-height: 1.7;
                        font-size: 15.5px;
                        box-shadow: 0 8px 20px rgba(14, 44, 83, 0.02);
                        word-break: break-word;
                        transition: all 0.2s ease;
                        font-family: "Droid Arabic Naskh", sans-serif;
                    }
                    .message-bubble.user {
                        background: linear-gradient(135deg, #92278f 0%, #b23cae 100%);
                        color: #ffffff;
                        border-radius: 20px 20px 0px 20px;
                        text-align: right;
                        box-shadow: 0 8px 20px rgba(146, 39, 143, 0.25);
                    }
                    .message-bubble.bot {
                        background-color: #fdfaf5;
                        color: #0e2c53;
                        border: 1px solid rgba(235, 217, 197, 0.7);
                        border-radius: 20px 20px 20px 0px;
                        text-align: right;
                    }
                    .bot-answer-html p {
                        margin-bottom: 10px;
                        font-size: 15.5px;
                        color: #0e2c53;
                        line-height: 1.8;
                    }
                    .bot-answer-html p:last-child {
                        margin-bottom: 0;
                    }
                    .bot-answer-html table {
                        width: 100%;
                        border-collapse: collapse;
                        margin: 15px 0;
                        border-radius: 12px;
                        overflow: hidden;
                        box-shadow: 0 4px 15px rgba(0,0,0,0.03);
                    }
                    .bot-answer-html th, .bot-answer-html td {
                        padding: 12px 15px;
                        text-align: right;
                        border-bottom: 1px solid #ebd9c5;
                    }
                    .bot-answer-html th {
                        background-color: #0e2c53;
                        color: #ffffff;
                    }
                    .bot-answer-html tr:last-child td {
                        border-bottom: none;
                    }
                    .bot-answer-html tr:nth-child(even) {
                        background-color: rgba(235, 217, 197, 0.15);
                    }
                    .message-stats {
                        display: flex;
                        gap: 10px;
                        font-size: 11px;
                        color: #8c857b;
                        margin-top: 6px;
                    }
                    .stats-badge {
                        background: rgba(241, 237, 228, 0.8);
                        padding: 3px 10px;
                        border-radius: 8px;
                        border: 1px solid rgba(235, 217, 197, 0.6);
                        direction: rtl;
                        display: inline-flex;
                        align-items: center;
                        gap: 4px;
                    }
                    .stats-badge i {
                        color: #ff5200;
                    }
                    .fullscreen-modal-overlay {
                        position: fixed;
                        top: 0;
                        left: 0;
                        right: 0;
                        bottom: 0;
                        background-color: rgba(14, 44, 83, 0.65);
                        backdrop-filter: blur(8px);
                        -webkit-backdrop-filter: blur(8px);
                        z-index: 10000;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        padding: 30px;
                    }
                    .fullscreen-modal-card {
                        background: rgba(255, 255, 255, 0.95);
                        border: 1px solid rgba(235, 217, 197, 0.6);
                        border-radius: 24px;
                        width: 100%;
                        max-width: 1000px;
                        height: 90vh;
                        display: flex;
                        flex-direction: column;
                        box-shadow: 0 30px 60px rgba(14, 44, 83, 0.25);
                        overflow: hidden;
                    }
                    .fullscreen-modal-header {
                        padding: 20px 30px;
                        border-bottom: 1px solid rgba(235, 217, 197, 0.5);
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        background-color: #fdfaf5;
                    }
                    .fullscreen-modal-title {
                        margin: 0;
                        font-size: 20px;
                        font-weight: 700;
                        color: #0e2c53;
                        display: flex;
                        align-items: center;
                        gap: 10px;
                        font-family: "Droid Arabic Naskh", sans-serif;
                    }
                    .fullscreen-modal-close-btn {
                        background: none;
                        border: none;
                        color: #ff5200;
                        font-size: 24px;
                        cursor: pointer;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        transition: all 0.2s ease;
                        width: 40px;
                        height: 40px;
                        border-radius: 50%;
                    }
                    .fullscreen-modal-close-btn:hover {
                        background-color: rgba(255, 82, 0, 0.1);
                        transform: scale(1.1);
                    }
                    .fullscreen-modal-body {
                        flex: 1;
                        padding: 40px;
                        overflow-y: auto;
                        text-align: right;
                        direction: rtl;
                    }
                    .bot-bubble-actions-row {
                        display: flex;
                        flex-direction: row;
                        align-items: center;
                        gap: 12px;
                        margin-top: 8px;
                        width: 100%;
                    }
                    .bot-bubble-actions {
                        display: flex;
                        flex-direction: row;
                        align-items: center;
                        gap: 8px;
                    }
                    .fullscreen-expand-btn, .bubble-download-btn {
                        background: #ffffff;
                        border: 1px solid rgba(235, 217, 197, 0.6);
                        color: #8c857b;
                        width: 30px;
                        height: 30px;
                        border-radius: 50%;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        cursor: pointer;
                        transition: all 0.2s ease;
                        box-shadow: 0 2px 5px rgba(14, 44, 83, 0.05);
                        outline: none;
                        font-size: 13px;
                    }
                    .fullscreen-expand-btn:hover {
                        background: #ffffff;
                        color: #92278f;
                        border-color: #92278f;
                        box-shadow: 0 4px 12px rgba(146, 39, 143, 0.15);
                        transform: scale(1.05);
                    }
                    .bubble-download-btn:hover {
                        background: #ffffff;
                        color: #8dc63f;
                        border-color: #8dc63f;
                        box-shadow: 0 4px 12px rgba(141, 198, 63, 0.15);
                        transform: scale(1.05);
                    }
                    .fullscreen-floating-download-btn {
                        position: sticky;
                        bottom: 20px;
                        float: left;
                        background: linear-gradient(135deg, #8dc63f 0%, #7bb32d 100%);
                        color: #ffffff !important;
                        width: 46px;
                        height: 46px;
                        border-radius: 50%;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        cursor: pointer;
                        box-shadow: 0 8px 20px rgba(141, 198, 63, 0.4);
                        border: none;
                        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                        z-index: 1000;
                        outline: none;
                    }
                    .fullscreen-floating-download-btn:hover {
                        background: linear-gradient(135deg, #7bb32d 0%, #689823 100%);
                        box-shadow: 0 12px 24px rgba(141, 198, 63, 0.5);
                        transform: scale(1.08);
                    }
                    .chat-input-area {
                        padding: 20px 25px 25px;
                        border-top: 1px solid rgba(235, 217, 197, 0.5);
                        background: rgba(253, 250, 245, 0.5);
                    }
                    .input-container {
                        position: relative;
                        display: flex;
                        flex-direction: column;
                        gap: 8px;
                    }
                    .input-textarea {
                        width: 100%;
                        border: 1px solid rgba(235, 217, 197, 0.8);
                        border-radius: 18px;
                        padding: 15px 18px;
                        font-size: 15.5px;
                        resize: none;
                        height: 65px;
                        outline: none;
                        font-family: inherit;
                        transition: all 0.25s ease;
                        background-color: #ffffff;
                        color: #0e2c53;
                        padding-left: 65px;
                        text-align: right;
                        box-shadow: 0 4px 12px rgba(14, 44, 83, 0.03);
                    }
                    .input-textarea:focus {
                        border-color: #92278f;
                        box-shadow: 0 0 0 4px rgba(146, 39, 143, 0.15);
                    }
                    .send-button {
                        position: absolute;
                        left: 12px;
                        top: 12px;
                        width: 40px;
                        height: 40px;
                        border-radius: 12px;
                        border: none;
                        background: linear-gradient(135deg, #8dc63f 0%, #7bb32d 100%);
                        color: #ffffff;
                        cursor: pointer;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        transition: all 0.3s ease;
                        font-size: 18px;
                        box-shadow: 0 4px 12px rgba(141, 198, 63, 0.3);
                    }
                    .send-button:hover:not(:disabled) {
                        background: linear-gradient(135deg, #7bb32d 0%, #689823 100%);
                        transform: translateY(-2px) rotate(-10deg);
                        box-shadow: 0 6px 16px rgba(141, 198, 63, 0.4);
                    }
                    .send-button:disabled {
                        background-color: #e2e8f0;
                        background-image: none;
                        color: #94a3b8;
                        cursor: not-allowed;
                        box-shadow: none;
                        transform: none;
                    }
                    .stop-button {
                        position: absolute;
                        left: 12px;
                        top: 12px;
                        width: 40px;
                        height: 40px;
                        border-radius: 12px;
                        border: none;
                        background: linear-gradient(135deg, #ff5200 0%, #d33c00 100%);
                        color: #ffffff;
                        cursor: pointer;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        transition: all 0.3s ease;
                        font-size: 18px;
                        box-shadow: 0 4px 12px rgba(255, 82, 0, 0.3);
                    }
                    .stop-button:hover {
                        background: linear-gradient(135deg, #d33c00 0%, #b82d00 100%);
                        transform: translateY(-2px);
                        box-shadow: 0 6px 16px rgba(255, 82, 0, 0.4);
                    }
                    .warning-message {
                        font-size: 12px;
                        color: #ff5200;
                        display: flex;
                        align-items: center;
                        gap: 5px;
                        margin-right: 5px;
                        font-family: "Droid Arabic Naskh", sans-serif;
                    }
                    .typing-indicator {
                        display: flex;
                        align-items: center;
                        gap: 5px;
                        padding: 8px 10px;
                    }
                    .typing-indicator span {
                        width: 8px;
                        height: 8px;
                        background: #92278f;
                        border-radius: 50%;
                        display: inline-block;
                        animation: bounce 1.3s infinite ease-in-out;
                    }
                    .typing-indicator span:nth-child(2) {
                        animation-delay: -1.1s;
                        background: #8dc63f;
                    }
                    .typing-indicator span:nth-child(3) {
                        animation-delay: -0.9s;
                        background: #ff5200;
                    }
                    @keyframes bounce {
                        0%, 80%, 100% { transform: scale(0); }
                        40% { transform: scale(1.0); }
                    }
                    .error-panel {
                        padding: 12px 18px;
                        border-radius: 14px;
                        font-size: 13.5px;
                        margin-bottom: 12px;
                        text-align: right;
                        font-family: "Droid Arabic Naskh", sans-serif;
                        display: flex;
                        align-items: center;
                        gap: 10px;
                        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
                        border: 1px solid transparent;
                        line-height: 1.5;
                    }
                    .error-panel.api_error {
                        background-color: #fffbeb;
                        border-color: #fde68a;
                        color: #b45309;
                    }
                    .error-panel.server_error {
                        background-color: #fef2f2;
                        border-color: #fecaca;
                        color: #dc2626;
                    }
                    .error-panel.network_error {
                        background-color: #f8fafc;
                        border-color: #e2e8f0;
                        color: #475569;
                    }
                    .error-panel.not_found {
                        background-color: #eff6ff;
                        border-color: #bfdbfe;
                        color: #1d4ed8;
                    }
                    .error-panel i {
                        font-size: 18px;
                    }
                    .chatbot-btn {
                        border-radius: 12px;
                        padding: 8px 18px;
                        font-size: 14px;
                        font-weight: 700;
                        cursor: pointer;
                        border: none;
                        transition: all 0.25s ease;
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        line-height: 1;
                        font-family: "Droid Arabic Naskh", sans-serif;
                    }
                    .chatbot-btn i {
                        margin-left: 4px;
                        margin-right: 0;
                    }
                    .chatbot-btn-primary {
                        background: linear-gradient(135deg, #92278f 0%, #b23cae 100%);
                        color: #ffffff;
                        box-shadow: 0 4px 12px rgba(146, 39, 143, 0.2);
                    }
                    .chatbot-btn-primary:hover {
                        background: linear-gradient(135deg, #791f76 0%, #9c3398 100%);
                        transform: translateY(-1px);
                        box-shadow: 0 6px 16px rgba(146, 39, 143, 0.3);
                    }
                    .chatbot-btn-danger {
                        background: linear-gradient(135deg, #ff5200 0%, #ff732f 100%);
                        color: #ffffff;
                        box-shadow: 0 4px 12px rgba(255, 82, 0, 0.2);
                    }
                    .chatbot-btn-danger:hover {
                        background: linear-gradient(135deg, #e04700 0%, #f75d16 100%);
                        transform: translateY(-1px);
                        box-shadow: 0 6px 16px rgba(255, 82, 0, 0.3);
                    }
                    .chatbot-btn-outline {
                        background-color: transparent;
                        border: 1px solid rgba(235, 217, 197, 0.8);
                        color: #8c857b;
                    }
                    .chatbot-btn-outline:hover {
                        background-color: rgba(255, 242, 223, 0.6);
                        border-color: rgba(235, 217, 197, 1);
                        color: #0e2c53;
                    }
                    .chatbot-btn-sm {
                        padding: 6px 14px;
                        font-size: 12.5px;
                        border-radius: 10px;
                    }
                    .sidebar-toggle-btn {
                        background: none;
                        border: none;
                        color: #0e2c53;
                        font-size: 20px;
                        cursor: pointer;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        width: auto;
                    }
                    .empty-chat-state {
                        flex: 1;
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        justify-content: center;
                        color: #8c857b;
                        text-align: center;
                        padding: 40px;
                        gap: 20px;
                    }
                    .empty-chat-icon-container {
                        width: 100px;
                        height: 100px;
                        border-radius: 50%;
                        background: rgba(146, 39, 143, 0.05);
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        margin-bottom: 10px;
                        animation: bounceEmpty 3s infinite ease-in-out;
                    }
                    .empty-chat-icon {
                        font-size: 44px;
                        color: #92278f;
                    }
                    @keyframes bounceEmpty {
                        0%, 100% { transform: translateY(0); }
                        50% { transform: translateY(-10px); }
                    }
                    .empty-chat-state h4 {
                        font-size: 20px;
                        font-weight: 700;
                        color: #0e2c53;
                        font-family: "Droid Arabic Naskh", sans-serif;
                    }
                    .empty-chat-state p {
                        font-size: 15px;
                        font-family: "Droid Arabic Naskh", sans-serif;
                    }
                    @media (max-width: 768px) {
                        .sidebar-toggle-btn {
                            display: flex;
                        }
                        .chatbot-sidebar {
                            position: absolute;
                            right: 0;
                            top: 0;
                            bottom: 0;
                            height: 100%;
                            width: 280px;
                            background-color: #fdfaf5;
                            z-index: 2100;
                            border-left: 1px solid rgba(235, 217, 197, 0.8);
                            box-shadow: -10px 0 30px rgba(14, 44, 83, 0.15);
                            transform: translateX(${showSidebar ? "0" : "100%"});
                        }
                        .chatbot-page-section {
                            padding: 0;
                            min-height: calc(100vh - 120px);
                            height: calc(100vh - 120px);
                            display: flex;
                            align-items: stretch;
                            justify-content: stretch;
                            width: 100%;
                            margin: 0;
                        }
                        .chatbot-wrapper {
                            height: 100%;
                            width: 100%;
                            border-radius: 0;
                            border: none;
                            max-width: 100%;
                            box-shadow: none;
                        }
                        .chatbot-main {
                            width: 100%;
                            height: 100%;
                        }
                        .chat-messages-area {
                            padding: 15px 12px;
                            gap: 16px;
                        }
                        .message-bubble {
                            padding: 12px 15px;
                            font-size: 14px;
                        }
                        .chat-header {
                            padding: 8px 12px;
                            min-height: 50px;
                            height: auto;
                            box-sizing: border-box;
                            display: flex;
                            align-items: center;
                            justify-content: space-between;
                        }
                        .chat-header-info {
                            gap: 8px;
                        }
                        .chat-header-title {
                            font-size: 14px;
                            line-height: 1.2;
                            display: flex;
                            align-items: center;
                        }
                        .chat-header-title i {
                            font-size: 15px;
                            margin-left: 4px;
                            margin-right: 0;
                        }
                        .chatbot-btn-sm {
                            padding: 6px 12px;
                            font-size: 12px;
                            border-radius: 8px;
                        }
                        .chat-input-area {
                            padding: 10px 12px;
                            background: #ffffff;
                            width: 100%;
                            box-sizing: border-box;
                        }
                        .input-textarea {
                            padding: 12px 14px;
                            padding-left: 56px;
                            font-size: 14px;
                            height: 52px;
                            border-radius: 12px;
                        }
                        .send-button, .stop-button {
                            left: 8px;
                            top: 8px;
                            width: 36px;
                            height: 36px;
                            border-radius: 10px;
                            font-size: 15px;
                        }
                        .fullscreen-modal-card {
                            height: 100vh;
                            max-height: 100vh;
                            border-radius: 0;
                            border: none;
                        }
                        .fullscreen-modal-body {
                            padding: 20px;
                        }
                        .fullscreen-modal-header {
                            padding: 15px 20px;
                        }
                        .stats-modal-card {
                            max-width: 100%;
                            width: 95%;
                            border-radius: 16px;
                        }
                    }
                    
                    /* Custom added styles */
                    .chat-input-area form {
                        width: 100%;
                    }
                    .input-container {
                        position: relative;
                        display: flex;
                        flex-direction: column;
                        gap: 8px;
                        width: 100%;
                        max-width: 100%;
                    }
                    .input-options-row {
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        margin-top: 10px;
                        margin-bottom: 5px;
                        direction: rtl;
                        text-align: right;
                        width: 100%;
                        gap: 15px;
                    }
                    .model-selector-container {
                        display: inline-flex;
                        align-items: center;
                    }
                    .model-select {
                        background-color: #ffffff;
                        border: 1px solid rgba(235, 217, 197, 0.8);
                        border-radius: 8px;
                        padding: 6px 12px;
                        font-size: 13px;
                        color: #0e2c53;
                        outline: none;
                        cursor: pointer;
                        transition: all 0.25s ease;
                        font-family: inherit;
                        font-weight: bold;
                    }
                    .model-select:focus {
                        border-color: #92278f;
                        box-shadow: 0 0 0 2px rgba(146, 39, 143, 0.15);
                    }
                    .model-select:disabled {
                        background-color: #f1f5f9;
                        color: #94a3b8;
                        cursor: not-allowed;
                    }
                    .passcash-label {
                        display: inline-flex;
                        align-items: center;
                        gap: 8px;
                        cursor: pointer;
                        user-select: none;
                        font-family: "Droid Arabic Naskh", sans-serif;
                        font-size: 13.5px;
                        color: #8c857b;
                        transition: color 0.2s ease;
                    }
                    .passcash-label:hover {
                        color: #0e2c53;
                    }
                    .passcash-checkbox {
                        width: 17px;
                        height: 17px;
                        accent-color: #92278f;
                        cursor: pointer;
                        border-radius: 4px;
                    }
                    .passcash-text {
                        line-height: 1;
                    }
                    .message-stats-container {
                        margin-top: 8px;
                        display: flex;
                        flex-direction: column;
                        gap: 8px;
                        align-items: flex-start;
                        width: 100%;
                    }
                    .stats-badge.total-time-badge {
                        cursor: pointer;
                        user-select: none;
                        display: inline-flex;
                        align-items: center;
                        gap: 5px;
                        background: rgba(146, 39, 143, 0.03);
                        border: 1px solid rgba(146, 39, 143, 0.1);
                        color: #92278f;
                        font-weight: 500;
                        transition: all 0.2s ease;
                        padding: 3px 8px;
                        border-radius: 6px;
                        font-size: 11px;
                        font-family: "Droid Arabic Naskh", sans-serif;
                    }
                    .stats-badge.total-time-badge:hover {
                        background: rgba(146, 39, 143, 0.07);
                       .bot-answer-streaming {           font-family: inherit;
                        color: #8dc63f;
                    }
                    .bot-answer-streaming {
                        font-size: 15.5px;
                        color: #0e2c53;
                        line-height: 1.8;
                        white-space: pre-wrap;
                    }
                    .fullscreen-modal-download-btn {
                        background: linear-gradient(135deg, #8dc63f 0%, #7bb32d 100%);
                        color: #ffffff !important;
                        box-shadow: 0 4px 12px rgba(141, 198, 63, 0.25);
                        border: none;
                        display: inline-flex;
                        align-items: center;
                        gap: 8px;
                        padding: 8px 16px;
                        border-radius: 10px;
                        font-size: 13px;
                        font-weight: 700;
                        cursor: pointer;
                        transition: all 0.25s ease;
                    }
                    .fullscreen-modal-download-btn:hover {
                        background: linear-gradient(135deg, #7bb32d 0%, #689823 100%);
                        box-shadow: 0 6px 16px rgba(141, 198, 63, 0.35);
                        transform: translateY(-1px);
                    }
                    .typing-indicator-container {
                        display: flex;
                        flex-direction: column;
                        gap: 8px;
                        align-items: flex-start;
                        padding: 5px 0;
                    }
                    .waiting-response-message {
                        font-size: 13px;
                        color: #8c857b;
                        display: flex;
                        align-items: center;
                        gap: 6px;
                        direction: rtl;
                        font-family: "Droid Arabic Naskh", sans-serif;
                    }
                    .waiting-timer {
                        color: #92278f;
                        font-weight: 700;
                        font-family: monospace, sans-serif;
                    }
                    h2 {
                        font-size: 28px;
                        line-height: 44px;
                    }
                    p{
                        padding: 5px 0px;
                    }
                    h3 {
                        margin-top:0px;
                    }
                `}} />

                <section className="chatbot-page-section">
                    {/* Background floating blobs */}
                    <div className="chatbot-blob blob-1"></div>
                    <div className="chatbot-blob blob-2"></div>

                    <div className="chatbot-wrapper">
                        {/* Sidebar: Chat History */}
                        <div className="chatbot-sidebar" style={!showSidebar ? { display: "none" } : {}}>
                            <div className="sidebar-header">
                                <div className="sidebar-title-row">
                                    <h3 className="sidebar-title">
                                        <i className="fal fa-comment-alt-lines"></i>
                                        {t("Pages.Chatbot.History")}
                                    </h3>
                                    <div className="sidebar-title-actions">
                                        {history.length > 0 && (
                                            <button
                                                type="button"
                                                className="sidebar-title-delete-btn"
                                                onClick={this.handleDeleteHistory}
                                                title={t("Pages.Chatbot.DeleteHistory")}
                                            >
                                                <i className="fal fa-trash-alt"></i>
                                            </button>
                                        )}
                                        {window.innerWidth < 768 && (
                                            <button className="sidebar-toggle-btn" onClick={this.toggleSidebar}>
                                                <i className="fal fa-times"></i>
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <div className="sidebar-search-group">
                                    <div className="sidebar-search-container">
                                        <i className="fal fa-search sidebar-search-icon"></i>
                                        <input
                                            type="text"
                                            className="sidebar-search-input"
                                            placeholder={t("Pages.Chatbot.SearchPlaceholder")}
                                            value={searchTerm}
                                            onChange={(e) => this.setState({ searchTerm: e.target.value })}
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        className="sidebar-new-chat-btn"
                                        onClick={this.handleNewChat}
                                        title={t("Pages.Chatbot.NewChat")}
                                    >
                                        <i className="fal fa-plus"></i>
                                    </button>
                                </div>
                            </div>
                            <div className="sidebar-history-list">
                                {filteredHistory.length === 0 ? (
                                    <div className="no-history-text">
                                        <i className="fal fa-box-open d-block mb-2 h3 text-muted"></i>
                                        {t("Pages.Chatbot.NoHistory")}
                                    </div>
                                ) : (
                                    filteredHistory.map(session => {
                                        const date = new Date(session.timestamp).toLocaleDateString("ar-EG", {
                                            month: "short",
                                            day: "numeric",
                                            hour: "2-digit",
                                            minute: "2-digit"
                                        });
                                        const isActive = session.id === activeSessionId;
                                        return (
                                            <div
                                                key={session.id}
                                                className={`history-item ${isActive ? "active" : ""}`}
                                                onClick={() => this.handleSessionClick(session)}
                                            >
                                                <div className="history-item-content">
                                                    <div className="history-item-title">{session.title}</div>
                                                    <div className="history-item-date">
                                                        <i className="fal fa-calendar-alt"></i>
                                                        <span>{date}</span>
                                                    </div>
                                                </div>
                                                <button
                                                    type="button"
                                                    className="history-item-delete-btn"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (window.confirm(t("Pages.Chatbot.ConfirmDeleteSession"))) {
                                                            this.props.deleteChatSession(session.id);
                                                        }
                                                    }}
                                                    title={t("Pages.Chatbot.DeleteHistory")}
                                                >
                                                    <i className="fal fa-trash-alt"></i>
                                                </button>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>

                        {/* Main Chat Area */}
                        <div className="chatbot-main">
                            <div className="chat-header">
                                <div className="chat-header-info">
                                    <h2 className="chat-header-title">
                                        <button className="sidebar-toggle-btn ml-2" onClick={this.toggleSidebar}>
                                            <i className="fal fa-indent"></i>
                                        </button>
                                        <i className="fal fa-robot text-primary ml-2"></i>
                                        {t("Pages.Chatbot.title")}
                                    </h2>
                                </div>
                                <div className="chat-header-actions">
                                    {messages.length > 0 && (
                                        <button
                                            className="chatbot-btn chatbot-btn-outline chatbot-btn-sm"
                                            onClick={this.handleClearChat}
                                            disabled={loading}
                                            title={t("Pages.Chatbot.Clear")}
                                        >
                                            <i className="fal fa-trash-undo"></i>
                                            <span className="d-none d-sm-inline">{t("Pages.Chatbot.Clear")}</span>
                                        </button>
                                    )}

                                </div>
                            </div>

                            <div className="chat-messages-area">
                                {messages.length === 0 ? (
                                    <div className="empty-chat-state">
                                        <div className="empty-chat-icon-container">
                                            <i className="fal fa-comments empty-chat-icon"></i>
                                        </div>
                                        <div>
                                            <h4>{t("Pages.Chatbot.title")}</h4>
                                            <p className="mt-2">{t("Pages.Chatbot.Subtitle")}</p>
                                        </div>
                                    </div>
                                ) : (
                                    messages.map((msg, index) => {
                                        const isBot = msg.sender === "bot";
                                        const totalTimeMs = (msg.databaseTimeMs || 0) + (msg.apiTimeMs || 0);
                                        const totalTimeSec = (totalTimeMs / 1000).toFixed(2);
                                        return (
                                            <div
                                                key={index}
                                                className={`message-row ${msg.sender} animate__animated animate__fadeInUp animate__faster`}
                                            >
                                                <div className="message-bubble-wrapper">
                                                    <div className={`message-bubble ${msg.sender}`}>
                                                        {isBot ? (
                                                            msg.text === "" && msg.isStreaming ? (
                                                                <div className="typing-indicator-container">
                                                                    <div className="typing-indicator">
                                                                        <span></span>
                                                                        <span></span>
                                                                        <span></span>
                                                                    </div>
                                                                    <div className="waiting-response-message">
                                                                        <span>{msg.statusText || t("Pages.Chatbot.WaitingResponse") || "جاري انتظار رد المساعد..."}</span>
                                                                        <span className="waiting-timer">({this.state.requestTimerSeconds} {t("Pages.Chatbot.Sec")})</span>
                                                                    </div>
                                                                </div>
                                                            ) : msg.isStreaming ? (
                                                                <div className="bot-answer-streaming">
                                                                    {msg.text.replace(/<[^>]*>?/gm, '')}
                                                                </div>
                                                            ) : (
                                                                <div
                                                                    className="bot-answer-html"
                                                                    dangerouslySetInnerHTML={{ __html: msg.text }}
                                                                />
                                                            )
                                                        ) : (
                                                            <div>{msg.text}</div>
                                                        )}
                                                    </div>
                                                    {isBot && !msg.isStreaming && msg.text !== "" && (
                                                        <div className="bot-bubble-actions-row">
                                                            {totalTimeMs > 0 && (
                                                                <div
                                                                    className="stats-badge total-time-badge"
                                                                    onClick={() => this.toggleStats(index)}
                                                                    role="button"
                                                                    tabIndex="0"
                                                                    title={t("Pages.Chatbot.TotalTime")}
                                                                    style={{ gap: "8px" }}
                                                                >
                                                                    <i className="fal fa-robot"></i>
                                                                    <span style={{ textTransform: "capitalize", fontWeight: "bold" }}>
                                                                        {msg.model || msg.Model || "deepseek"}
                                                                    </span>
                                                                    <span style={{ opacity: 0.3 }}>|</span>
                                                                    <i className="fal fa-clock"></i>
                                                                    <span>
                                                                        {t("Pages.Chatbot.TotalTime")}: {totalTimeSec} {t("Pages.Chatbot.Sec")}
                                                                    </span>
                                                                </div>
                                                            )}
                                                            <div className="bot-bubble-actions">
                                                                <button
                                                                    type="button"
                                                                    className="fullscreen-expand-btn"
                                                                    onClick={() => this.setState({
                                                                        fullscreenMessageHtml: msg.text,
                                                                        fullscreenMessageQuestion: messages[index - 1] ? messages[index - 1].text : ""
                                                                    })}
                                                                    title={t("Pages.Chatbot.FullScreen")}
                                                                >
                                                                    <i className="fal fa-expand-arrows"></i>
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className="bubble-download-btn"
                                                                    onClick={() => this.handleDownloadResult(msg.text, messages[index - 1] ? messages[index - 1].text : "")}
                                                                    title={t("Pages.Chatbot.Download")}
                                                                >
                                                                    <i className="fal fa-download"></i>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                                <div ref={this.messagesEndRef} />
                            </div>

                            <div className="chat-input-area">
                                {error && (
                                    <div className={`error-panel ${errorStatus || ""}`}>
                                        <i className={
                                            errorStatus === "api_error"
                                                ? "fal fa-robot animate__animated animate__shakeX"
                                                : errorStatus === "server_error"
                                                    ? "fal fa-server animate__animated animate__shakeX"
                                                    : errorStatus === "not_found"
                                                        ? "fal fa-exclamation-circle animate__animated animate__shakeX"
                                                        : "fal fa-wifi-slash animate__animated animate__flash animate__infinite"
                                        }></i>
                                        <div style={{ flex: 1 }}>
                                            <strong>
                                                {errorStatus === "api_error"
                                                    ? t("Pages.Chatbot.AiError")
                                                    : errorStatus === "server_error"
                                                        ? t("Pages.Chatbot.ServerError")
                                                        : errorStatus === "not_found"
                                                            ? t("Pages.Chatbot.NotFoundError")
                                                            : t("Pages.Chatbot.ConnectionError")}
                                            </strong>
                                            {errorStatus !== "not_found" && error}
                                        </div>
                                        {errorStatus !== "not_found" && (
                                            <button
                                                type="button"
                                                className="chatbot-btn chatbot-btn-primary chatbot-btn-sm"
                                                onClick={this.handleResend}
                                                style={{ alignSelf: "center" }}
                                            >
                                                <i className="fal fa-redo"></i>
                                                <span>{t("Pages.Chatbot.Resend")}</span>
                                            </button>
                                        )}
                                    </div>
                                )}
                                <ChatInput onSend={this.props.sendChatMessage} onStop={this.props.stopChatStream} loading={loading} t={t} />
                            </div>
                        </div>
                    </div>
                    {fullscreenMessageHtml && (
                        <div className="fullscreen-modal-overlay animate__animated animate__fadeIn animate__faster" onClick={() => this.setState({ fullscreenMessageHtml: null, fullscreenMessageQuestion: null })}>
                            <div className="fullscreen-modal-card animate__animated animate__zoomIn animate__faster" onClick={(e) => e.stopPropagation()}>
                                <div className="fullscreen-modal-header">
                                    <h3 className="fullscreen-modal-title">
                                        <i className="fal fa-robot text-primary"></i>
                                        {t("Pages.Chatbot.FullScreenTitle")}
                                    </h3>
                                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                        <button
                                            type="button"
                                            className="fullscreen-modal-download-btn"
                                            onClick={() => this.handleDownloadResult(fullscreenMessageHtml, this.state.fullscreenMessageQuestion)}
                                            title={t("Pages.Chatbot.Download")}
                                        >
                                            <i className="fal fa-download"></i>
                                            <span>{t("Pages.Chatbot.Download")}</span>
                                        </button>
                                        <button
                                            className="fullscreen-modal-close-btn"
                                            onClick={() => this.setState({ fullscreenMessageHtml: null, fullscreenMessageQuestion: null })}
                                            title={t("cancel")}
                                        >
                                            <i className="fal fa-times"></i>
                                        </button>
                                    </div>
                                </div>
                                <div className="fullscreen-modal-body bot-answer-html" dangerouslySetInnerHTML={{ __html: fullscreenMessageHtml }} />
                            </div>
                        </div>
                    )}
                    <AiStatsModal
                        isOpen={expandedStatsIndex !== -1 && messages[expandedStatsIndex] !== undefined}
                        onClose={() => this.setState({ expandedStatsIndex: -1 })}
                        stats={messages[expandedStatsIndex]}
                        t={t}
                    />
                </section>
                <Footer />
            </>
        );
    }
}

const mapStateToProps = state => {
    return {
        messages: state.chatbot.messages,
        history: state.chatbot.history,
        loading: state.chatbot.loading,
        databaseTimeMs: state.chatbot.databaseTimeMs,
        apiTimeMs: state.chatbot.apiTimeMs,
        error: state.chatbot.error,
        errorStatus: state.chatbot.errorStatus,
        activeSessionId: state.chatbot.activeSessionId
    };
};

export default connect(mapStateToProps, {
    initChatbot,
    deleteChatHistory,
    deleteChatSession,
    clearActiveChat,
    startNewChat,
    loadChatSession,
    stopChatStream,
    sendChatMessage
})(withTranslation("common")(Chatbot));
